import React, { useState } from 'react';

import { type CSTypes } from '@tf/api';
import { Box, Button, Icon, Input, Stack, TextInput, TFText, Title } from '@tf/ui';
import { fmt, saveFile, splitObjectId, T } from '@tf/utils';

import { downloadFile } from '@/core/api/files';
import type { IRPatch } from '@/components/internal-tools/configurationPreview';
import {
	getPreviewLink,
	useExistingIRPatches,
	useUploadIRPatchMutation,
} from '@/components/internal-tools/configurationPreview';
import { useGoogleAPI } from '@/components/internal-tools/google';
import { Log, useLogger } from '@/components/internal-tools/log';

const spreadsheetIdRegex = /spreadsheets\/d\/([\w-]+)\/edit/gm;

const extractSpreadsheetId = (fileUrl: string) => {
	const matches = spreadsheetIdRegex.exec(fileUrl);
	return matches?.[1] ? matches[1] : fileUrl;
};

type Table = string[][];

const readTables = async (spreadsheetId: string, ranges: string[]) => {
	const valuesRes = await gapi.client.sheets.spreadsheets.values.batchGet({ spreadsheetId, ranges });

	const tables: Table[] = [];
	valuesRes.result.valueRanges?.forEach((valueRange, index) => {
		// Skip first info sheet
		if (valueRange.range?.startsWith('Sheet1') || index === 0) {
			return;
		}

		if (!valueRange.values) {
			return;
		}

		for (let i = 0; i < valueRange.values.length; i++) {
			const table: Table = [];
			while (i < valueRange.values.length && valueRange.values[i].length > 0) {
				table.push(valueRange.values[i]);
				i++;
			}
			tables.push(table);
		}
	});

	return tables;
};

const tablesToIRPatch = (tables: Table[]) => {
	const irPatch: IRPatch = { structs: {}, forms: [], options: {} };

	const getHeader = (table: Table) => {
		const type = table[0][0];
		const name = table[0][1];
		if (!type || !name) {
			throw new Error(
				`Wrong header for table, expected type and name. Got type = ${type}, name = ${name}`
			);
		}
		return { type, name };
	};

	const refToType: Record<string, string> = {};
	tables.forEach((table) => {
		const { type, name } = getHeader(table);
		if (type === 'struct' || type === 'enum' || type === 'document') {
			refToType[name] = type;
		}
	});

	tables.forEach((table) => {
		const { type, name } = getHeader(table);
		switch (type) {
			case 'form': {
				irPatch.forms.push({
					name,
					label: table[2][1],
					order: parseInt(table[3][1], 10),
					segments: table[4][1].split('\n').map((kind) => ({ kind })),
				});
				break;
			}

			case 'enum': {
				irPatch.options[name] = [];
				for (let i = 2; i < table.length; i++) {
					irPatch.options[name].push({ label: table[i][0], value: table[i][1] });
				}
				break;
			}

			case 'struct':
			case 'document':
			case 'segment': {
				irPatch.structs[name] = {
					name,
					label: '',
					kind: type === 'struct' ? 'default' : type,
					fields: [],
					// @ts-expect-error no type def for field
					visual: { preset: table[0][2] },
				};

				for (let i = 2; i < table.length; i++) {
					const tableType = table[i][2];
					const refType = refToType[tableType];

					const typeFields: {
						kind: CSTypes.FieldKind;
						type: CSTypes.FieldType;
						ref?: string;
						isMultiple?: boolean;
					} =
						tableType === 'info' || tableType === 'label'
							? {
									kind: 'display_only',
									type: tableType,
							  }
							: refType === 'enum'
							? {
									kind: 'enum',
									type: 'enum',
									ref: tableType,
									isMultiple: table[i][3] === 'TRUE',
							  }
							: refType === 'struct' || refType === 'document'
							? {
									kind: 'struct',
									type: 'struct',
									ref: tableType,
									isMultiple: table[i][3] === 'TRUE',
							  }
							: { kind: 'primitive', type: tableType as CSTypes.FieldType };

					irPatch.structs[name].fields.push({
						...typeFields,
						name: table[i][1],
						visual: {
							label: table[i][0],
							order: (i - 2) * 10,
							preset: table[i][4],
							tooltip: table[i][5],
						},
					});
				}
				break;
			}
			default: {
				throw new Error(`Unknown type ${type}`);
			}
		}
	});

	return irPatch;
};

export const ImportRoute: React.FC = () => {
	const { getToken } = useGoogleAPI();

	const [fileURL, setFileURL] = useState<string>('');

	const existingIRPatches = useExistingIRPatches();

	const uploadIRPatchMutation = useUploadIRPatchMutation();

	const { logMessages, clearLog, addLogMessage } = useLogger();

	const runImport = () => {
		clearLog();

		getToken()
			.then(async () => {
				const spreadsheetId = extractSpreadsheetId(fileURL);
				addLogMessage(`Starting import, spreadsheet ID: ${spreadsheetId}`);

				const spreadsheetRes = await gapi.client.sheets.spreadsheets.get({ spreadsheetId });
				const ranges =
					spreadsheetRes.result.sheets?.map((s) => s.properties?.title).filter(T.isNotNullish) ?? [];
				const tables = await readTables(spreadsheetId, ranges);
				const irPatch = tablesToIRPatch(tables);

				addLogMessage('IR patch created, uploading to GG');

				// TODO: list all previous files
				const irPathFileId = await uploadIRPatchMutation.mutateAsync({
					irPatch,
					filename: spreadsheetRes.result.properties?.title ?? spreadsheetId,
				});
				if (!irPathFileId) {
					throw new Error('Upload to GG failed');
				}

				const { objectName } = splitObjectId(irPathFileId);
				const previewLink = getPreviewLink(objectName);
				addLogMessage(
					<>
						Preview link:{' '}
						<a href={previewLink} target="_blank" rel="noreferrer">
							{previewLink}
						</a>
					</>
				);
				addLogMessage('Done');
			})
			.catch((err) => {
				addLogMessage(`ERROR: ${JSON.stringify(err)}`);
			});
	};

	return (
		<Stack gap="8px">
			<Box>
				<Title order={2}>Import from Google Sheets</Title>
				<TFText maw="60ch">This will ask you to authorize with your Google account</TFText>
			</Box>

			<Box sx={{ display: 'flex', gap: '8px', alignItems: 'end' }}>
				<Input.Wrapper label="Filename" id="filename">
					<TextInput
						id="filename"
						placeholder="Spreadsheet ID or url"
						value={fileURL}
						onChange={(e) => {
							setFileURL(e.target.value);
						}}
					/>
				</Input.Wrapper>
				<Button onClick={runImport}>Import</Button>
			</Box>

			<Log logMessages={logMessages} />

			{existingIRPatches.data?.length ? (
				<Stack gap="8px">
					<Title order={2}>Previous imports</Title>
					{existingIRPatches.data.map((file) => {
						const { objectName } = splitObjectId(file.id);
						const previewLink = getPreviewLink(objectName);

						return (
							<Stack key={file.id} gap="2px" align="start">
								<Box>File: {file.meta['@filename']}</Box>
								<Box>Date: {fmt.toDate(file.meta['@createdAt'], { preset: 'full_date_with_time' })}</Box>
								<Box>
									Preview link:{' '}
									<a href={previewLink} target="_blank" rel="noreferrer">
										{previewLink}
									</a>
								</Box>
								<Button
									variant="light"
									size="sm"
									onClick={async () => {
										const blob = await downloadFile(file.id);
										saveFile(blob, file.meta['@filename']);
									}}
									rightSection={<Icon.IconDownload size="18" />}
								>
									Download IR patch
								</Button>
							</Stack>
						);
					})}
				</Stack>
			) : null}
		</Stack>
	);
};
